import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code as ConnectCode, ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraCheckbox, SpecraDataTable, SpecraEyebrow, SpecraInlineBanner, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Check } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { contractModelClient } from '../../../lib/connect/clients';
import { useChatFeedback } from '../ChatFeedbackContext';
import { recalledDecision, rememberDecision } from './proposalDecisions';
const CAUSES = {
    document_error: 'Document error',
    client_change: 'Client change',
    not_performed: 'Not performed',
    linking: 'Linking (one artifact)'
};
/** Each kind's reason and default cause, as the run stages them. */ const KINDS = {
    add: {
        label: 'Add',
        reason: 'The evidence files it; the documents do not create it for this team',
        cause: 'document_error'
    },
    waive: {
        label: 'Waive',
        reason: 'Not tracked or delivered per client tracker',
        cause: 'not_performed'
    },
    cover: {
        label: 'Cover',
        reason: "Its records file another team's document: one artifact, so it watches that team's dates",
        cause: 'linking'
    },
    lineage: {
        label: 'Is this the predecessor?',
        reason: "Its records predate the team their label names; they file on its predecessor's dates, never the successor's",
        cause: 'linking'
    }
};
const UNDECIDED = {
    NO_TEAM_LABEL: 'No team on the row',
    UNKNOWN_TEAM: 'Team not in this program',
    AMBIGUOUS_TEAM: 'Team label fits several teams',
    NO_TITLE: 'No deliverable title',
    AMBIGUOUS_DUTY: 'Title fits several deliverables',
    NO_DUE_DATE: 'No due date',
    NO_OCCURRENCE_ON_DATE: 'No date near the stated one',
    OCCURRENCE_TAKEN: 'Date already filed by another row',
    PROGRAM_HAS_NO_DATES: 'The program has it but no dates: fix the schedule',
    TRACKED_BUT_NO_ROW: 'Tracked, but no row for this date',
    OVER_BATCH_LIMIT: 'More than one batch holds: close these first'
};
const ASSIST_HEADERS = {
    'X-Specra-Via': 'assist'
};
function kindOf(g) {
    if (g.kind === 'cover') return KINDS.cover;
    if (g.kind === 'lineage') return KINDS.lineage;
    return g.kind === 'add' ? KINDS.add : KINDS.waive;
}
function n(v) {
    return typeof v === 'number' ? v : 0;
}
/** The pattern a gap follows: its kind, and for a waiver or cover, why. */ function patternOf(g) {
    if (g.kind === 'waive') {
        if (g.series && g.cause === 'client_change') return 'Waive: the client’s own cadence, dated on Accept';
        if (g.series) return 'Waive: never tracked, dated on Accept';
        return g.reason ? 'Waive: the client’s own cadence' : 'Waive: never tracked';
    }
    if (g.kind === 'cover') {
        if (g.id.startsWith('b')) return 'Cover: the contract’s own deliverable';
        if (g.id.startsWith('r')) return 'Cover: routed by another term’s SOW';
        if (g.id.startsWith('k')) return 'Cover: filed under another label';
        return 'Cover: one artifact';
    }
    return kindOf(g).label;
}
/**
 * The groups a user ticks or unticks at once (R20-A): each pattern, then
 * each deliverable two or more gaps share (a kickoff agenda across teams).
 */ export function gapGroups(gaps) {
    const by = new Map();
    const add = (key, label, id)=>{
        const g = by.get(key) ?? {
            key,
            label,
            ids: []
        };
        g.ids.push(id);
        by.set(key, g);
    };
    for (const g of gaps)add(`p:${patternOf(g)}`, patternOf(g), g.id);
    for (const g of gaps)if (g.title) add(`t:${g.title}`, g.title, g.id);
    return [
        ...by.values()
    ].filter((g)=>g.key.startsWith('p:') || g.ids.length > 1);
}
/** One line on what closing the gaps wrote. */ export function gapsReceipt(plan, skipped) {
    const parts = [
        `${n(plan.deliverablesNew)} deliverables added with ${n(plan.datesNew)} dates`,
        `${n(plan.submissionsNew)} submissions filed on them`
    ];
    if (n(plan.rulesNew)) parts.splice(1, 0, `${n(plan.rulesNew)} with a team schedule, so later dates project`);
    if (n(plan.waiversInstant)) parts.push(`${n(plan.waiversInstant)} dates waived now (instant: ${plan.instantApprover})`);
    if (n(plan.waivers)) parts.push(`${n(plan.waivers)} waivers proposed for a second approver`);
    if (n(plan.waiversExisting)) parts.push(`${n(plan.waiversExisting)} already waived or proposed`);
    if (n(plan.coversNew)) parts.push(`${n(plan.coversNew)} SOWs now watch the producer's dates (covered)`);
    if (n(plan.datesCovered)) parts.push(`${n(plan.datesCovered)} dates covered by the producer's own deliverable`);
    if (skipped) parts.push(`${skipped} gaps left as you unticked them`);
    if (n(plan.conflicts)) parts.push(`${n(plan.conflicts)} left as questions`);
    return `${parts.join(', ')}.`;
}
function errorMessage(err) {
    if (err instanceof ConnectError) {
        if (err.code === ConnectCode.PermissionDenied) return err.rawMessage || "You can't add deliverables on this program.";
        if (err.code === ConnectCode.NotFound || err.code === ConnectCode.FailedPrecondition) return err.rawMessage || 'The program changed since this was staged. Ask to stage it again.';
    }
    return "The gaps couldn't be closed right now. Retry.";
}
/** What closing the gaps wrote, as the card shows it, from a run's plan. */ function receiptOf(p) {
    return gapsReceipt({
        deliverablesNew: p.deliverablesNew,
        datesNew: p.datesNew,
        rulesNew: p.rulesNew,
        submissionsNew: p.submissionsNew,
        waivers: p.waivers,
        waiversInstant: p.waiversInstant,
        waiversExisting: p.waiversExisting,
        instantApprover: p.instantApprover,
        conflicts: p.conflicts.length,
        coversNew: p.coversNew,
        datesCovered: p.datesCovered
    }, p.gapsSkipped);
}
/**
 * An evidence batch's second, separate batch: the gaps the filings leave.
 * Each proposed add or waiver shows its reason, cause tag and source and can
 * be unticked or retagged; what code could not close is listed as needing a
 * decision. Nothing is written until its own Accept, and a run applied
 * already (a reload, another tab) shows what it wrote, never Accept again.
 */ export function EvidenceGapsSection({ output, toolCallId, withReviews }) {
    const sendFeedback = useChatFeedback();
    const key = toolCallId ? `${toolCallId}:gaps` : undefined;
    const recalled = recalledDecision(key);
    const [status, setStatus] = useState(recalled?.decision === 'accepted' ? 'accepted' : 'pending');
    const [receipt, setReceipt] = useState(recalled?.receipt ?? '');
    const [error, setError] = useState(null);
    // Client state until Accept: ticking is instant, one save closes the run.
    const [skipped, setSkipped] = useState(()=>new Set((output.gaps ?? []).filter((g)=>g.off).map((g)=>g.id)));
    const [causes, setCauses] = useState({});
    // The run itself is the truth: a gap run applied already is shown applied,
    // ticked as it was accepted (R22-A), never as first proposed.
    const [runChecked, setRunChecked] = useState(false);
    const actingRef = useRef(false);
    const { programId, gapRunId } = output;
    // biome-ignore lint/correctness/useExhaustiveDependencies: read once per run; later status changes are the section's own
    useEffect(()=>{
        if (!programId || !gapRunId) {
            setRunChecked(true);
            return;
        }
        let live = true;
        contractModelClient.importTracker({
            body: {
                programId,
                runId: gapRunId,
                dryRun: true
            }
        }, {
            headers: ASSIST_HEADERS
        }).then((resp)=>{
            const applied = resp.body?.applied ? resp.body.plan : undefined;
            if (!live || !applied) return;
            setSkipped(new Set(applied.gapsSkippedIds));
            const line = receiptOf(applied);
            rememberDecision(key, {
                decision: 'accepted',
                receipt: line
            });
            setReceipt(line);
            setStatus('accepted');
        }).catch(()=>{
        // Unread: Accept stays, and closing an applied run writes nothing.
        }).finally(()=>{
            if (live) setRunChecked(true);
        });
        return ()=>{
            live = false;
        };
    }, [
        programId,
        gapRunId
    ]);
    const gaps = output.gaps ?? [];
    const groups = useMemo(()=>gapGroups(output.gaps ?? []), [
        output.gaps
    ]);
    const tick = (ids, on)=>setSkipped((prev)=>{
            const next = new Set(prev);
            for (const id of ids){
                if (on) next.delete(id);
                else next.add(id);
            }
            return next;
        });
    const undecided = (output.gapsUndecided ?? []).map((u, i)=>({
            id: String(i),
            ...u
        }));
    if (!gaps.length && !undecided.length && !output.gapError) return null;
    const kept = gaps.filter((g)=>!skipped.has(g.id));
    const adds = kept.filter((g)=>g.kind === 'add');
    const waives = kept.filter((g)=>g.kind === 'waive');
    const covers = kept.filter((g)=>g.kind === 'cover');
    const lineages = kept.filter((g)=>g.kind === 'lineage');
    const plan = output.gapPlan ?? {};
    const accepted = status === 'accepted';
    const close = async ()=>{
        if (actingRef.current || status !== 'pending' || !output.gapRunId || !kept.length) return;
        actingRef.current = true;
        setStatus('working');
        setError(null);
        try {
            const gapCauses = {};
            for (const [id, c] of Object.entries(causes))if (!skipped.has(id)) gapCauses[id] = c;
            const resp = await contractModelClient.importTracker({
                body: {
                    programId: output.programId ?? '',
                    runId: output.gapRunId,
                    closeGaps: true,
                    skipGaps: [
                        ...skipped
                    ],
                    gapCauses,
                    includeReviews: withReviews
                }
            }, {
                headers: ASSIST_HEADERS
            });
            const p = resp.body?.plan;
            if (p) setSkipped(new Set(p.gapsSkippedIds));
            const line = p ? receiptOf(p) : 'Closed.';
            rememberDecision(key, {
                decision: 'accepted',
                receipt: line
            });
            setReceipt(line);
            setStatus('accepted');
            sendFeedback(`[evidence-gaps] applied: ${line}`);
        } catch (err) {
            setStatus('pending');
            setError(errorMessage(err));
        } finally{
            actingRef.current = false;
        }
    };
    const waiverLine = n(plan.waiversInstant) ? `waived now (instant: ${plan.instantApprover})` : 'proposed for a second approver';
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.assistant.evidenceGaps,
        className: "border-t border-border",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1 px-4 py-2.5",
                children: [
                    /*#__PURE__*/ _jsx(SpecraEyebrow, {
                        children: accepted ? 'Gaps closed' : 'Close the gaps · a separate batch · nothing written yet'
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        size: "meta",
                        children: [
                            covers.length ? `Cover ${covers.length} teams by another's artifact · ` : '',
                            lineages.length ? `Ask ${lineages.length} predecessor questions (${lineages.reduce((s, g)=>s + n(g.dates), 0)} records) · ` : '',
                            "Add ",
                            adds.length,
                            " deliverables (",
                            adds.reduce((s, g)=>s + n(g.dates), 0),
                            " dates) · Waive",
                            ' ',
                            waives.length,
                            " series (",
                            waives.reduce((s, g)=>s + n(g.dates), 0),
                            " dates, ",
                            waiverLine,
                            ") · ",
                            n(output.gapsUndecidedRows),
                            " rows and ",
                            n(output.gapsUndecidedDates),
                            " dates need a decision"
                        ]
                    })
                ]
            }),
            output.gapError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                variant: "band",
                tone: "warning",
                children: output.gapError
            }) : null,
            gaps.length ? /*#__PURE__*/ _jsxs("details", {
                className: "border-t border-border px-4 py-2",
                open: !accepted,
                children: [
                    /*#__PURE__*/ _jsx("summary", {
                        className: "cursor-pointer",
                        children: /*#__PURE__*/ _jsxs(SpecraText, {
                            size: "meta",
                            as: "span",
                            children: [
                                "Gaps to close (",
                                kept.length,
                                " of ",
                                gaps.length,
                                " ticked)"
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx("fieldset", {
                        className: "flex flex-wrap gap-x-4 gap-y-1 border-0 p-0 py-2",
                        "aria-label": "Tick or untick a group",
                        children: groups.map((grp)=>{
                            const kept = grp.ids.filter((id)=>!skipped.has(id)).length;
                            return(// specra-allow raw-element: wraps the checkbox so its group name ticks it too
                            /*#__PURE__*/ _jsxs("label", {
                                className: "flex items-center gap-1.5",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraCheckbox, {
                                        "data-testid": Testids.assistant.evidenceGapGroup,
                                        "aria-label": `Close every gap: ${grp.label}`,
                                        checked: kept === grp.ids.length ? true : kept === 0 ? false : 'indeterminate',
                                        disabled: status !== 'pending',
                                        onCheckedChange: (c)=>tick(grp.ids, c === true)
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        size: "micro",
                                        as: "span",
                                        children: [
                                            grp.label,
                                            " (",
                                            kept,
                                            "/",
                                            grp.ids.length,
                                            ")"
                                        ]
                                    })
                                ]
                            }, grp.key));
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraDataTable, {
                        columns: [
                            {
                                id: 'keep',
                                header: '',
                                cell: (g)=>/*#__PURE__*/ _jsx(SpecraCheckbox, {
                                        "data-testid": Testids.assistant.evidenceGapItem,
                                        "aria-label": `Close ${g.title ?? 'this gap'}`,
                                        checked: !skipped.has(g.id),
                                        disabled: status !== 'pending',
                                        onCheckedChange: (c)=>tick([
                                                g.id
                                            ], c === true)
                                    })
                            },
                            {
                                id: 'kind',
                                header: 'Do',
                                cell: (g)=>kindOf(g).label
                            },
                            {
                                id: 'team',
                                header: 'Team',
                                cell: (g)=>output.gapTeams?.[g.team ?? -1] || '—'
                            },
                            {
                                id: 'title',
                                header: 'Deliverable',
                                cell: (g)=>g.kind === 'cover' && g.producer ? `${g.title || '—'} (produced by ${g.producer})` : g.kind === 'lineage' && g.label ? `${g.title || '—'} (labelled ${g.label})` : g.title || '—'
                            },
                            {
                                id: 'dates',
                                header: 'Dates',
                                cell: (g)=>g.series ? 'On Accept' : String(n(g.dates))
                            },
                            {
                                id: 'reason',
                                header: 'Why',
                                cell: (g)=>g.reason || kindOf(g).reason
                            },
                            {
                                id: 'cause',
                                header: 'Cause',
                                cell: (g)=>/*#__PURE__*/ _jsx(SpecraSelect, {
                                        size: "sm",
                                        "aria-label": "Cause",
                                        value: causes[g.id] ?? g.cause ?? kindOf(g).cause,
                                        disabled: status !== 'pending',
                                        onChange: (e)=>setCauses({
                                                ...causes,
                                                [g.id]: e.target.value
                                            }),
                                        children: Object.entries(CAUSES).map(([v, label])=>/*#__PURE__*/ _jsx("option", {
                                                value: v,
                                                children: label
                                            }, v))
                                    })
                            },
                            {
                                id: 'source',
                                header: 'Source',
                                cell: (g)=>g.source || ''
                            }
                        ],
                        rows: gaps,
                        getRowId: (g)=>g.id,
                        framed: false
                    })
                ]
            }) : null,
            undecided.length ? /*#__PURE__*/ _jsxs("details", {
                className: "border-t border-border px-4 py-2",
                children: [
                    /*#__PURE__*/ _jsx("summary", {
                        className: "cursor-pointer",
                        children: /*#__PURE__*/ _jsxs(SpecraText, {
                            size: "meta",
                            as: "span",
                            children: [
                                "Needs a decision (",
                                n(output.gapsUndecidedRows),
                                " rows, ",
                                n(output.gapsUndecidedDates),
                                ' ',
                                "dates)"
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "micro",
                        tone: "muted",
                        as: "p",
                        className: "py-1",
                        children: Object.entries(output.gapsUndecidedByWhy ?? {}).sort((a, b)=>b[1] - a[1]).map(([why, count])=>`${UNDECIDED[why] ?? why}: ${count}`).join(' · ')
                    }),
                    /*#__PURE__*/ _jsx(SpecraDataTable, {
                        columns: [
                            {
                                id: 'n',
                                header: 'Rows / dates',
                                cell: (u)=>String(n(u.rows) + n(u.dates))
                            },
                            {
                                id: 'why',
                                header: 'Why',
                                cell: (u)=>UNDECIDED[u.why ?? ''] ?? u.why
                            },
                            {
                                id: 'team',
                                header: 'Team',
                                cell: (u)=>u.team || '—'
                            },
                            {
                                id: 'title',
                                header: 'Deliverable',
                                cell: (u)=>u.title || '—'
                            }
                        ],
                        rows: undecided,
                        getRowId: (u)=>u.id,
                        framed: false
                    })
                ]
            }) : null,
            error ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                variant: "band",
                tone: "danger",
                "data-testid": Testids.assistant.evidenceGapsResult,
                children: error
            }) : null,
            output.gapRunId && !accepted ? /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2 border-t border-border bg-muted/40 px-4 py-3",
                children: [
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        "data-testid": Testids.assistant.evidenceGapsAccept,
                        onClick: ()=>void close(),
                        disabled: !kept.length || !toolCallId || !runChecked,
                        isLoading: status === 'working' || !runChecked,
                        iconLeft: /*#__PURE__*/ _jsx(Check, {
                            className: "h-4 w-4"
                        }),
                        children: [
                            "Close ",
                            kept.length,
                            " gaps"
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "micro",
                        tone: "muted",
                        className: "ml-auto",
                        children: "Untick a gap to leave it; nothing is written until you choose."
                    })
                ]
            }) : null,
            accepted ? /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.assistant.evidenceGapsResult,
                className: "flex items-start gap-2 border-t border-border bg-muted/40 px-4 py-3",
                children: [
                    /*#__PURE__*/ _jsx(Check, {
                        className: "mt-0.5 h-3.5 w-3.5 shrink-0 text-success",
                        "aria-hidden": true
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "micro",
                        className: "font-medium",
                        children: receipt || 'Closed.'
                    })
                ]
            }) : null
        ]
    });
}
